import React from 'react';
import { Tooltip } from '@mui/material';

/**
 * Parse translation text containing tooltip markers and return React nodes
 * Pattern: [[trigger||tooltip content]]
 * Example: "Smoking history ([[PYs||Packs per year]])"
 *
 * Falls back to plain text if:
 * - No tooltip markers found
 * - Malformed delimiters (missing pipes, unmatched brackets)
 * - Any parsing errors
 */
export const parseTooltipText = (text: string): React.ReactNode => {
  try {
    // Pattern to match [[trigger||tooltip]]
    // Must have exactly 2 pipes and proper closing
    const tooltipPattern = /\[\[([^\]|]+)\|\|([^\]]+)]]/g;

    if (!text.includes('[[') || !text.includes(']]')) {
      return text;
    }

    // any valid matches
    const testMatches = text.match(tooltipPattern);
    if (!testMatches || testMatches.length === 0) {
      // return as-is so translator sees issue
      return text;
    }

    // Reset regex for actual parsing
    tooltipPattern.lastIndex = 0;

    const parts: React.ReactNode[] = [];
    let lastIndex = 0;
    let match;
    let key = 0;

    while ((match = tooltipPattern.exec(text)) !== null) {
      // Add text before the tooltip
      if (match.index > lastIndex) {
        parts.push(text.slice(lastIndex, match.index));
      }

      const trigger = match[1].trim();
      const tooltipContent = match[2].trim();

      // Skip if trigger or tooltip is empty
      if (!trigger || !tooltipContent) {
        parts.push(match[0]);
        lastIndex = match.index + match[0].length;
        continue;
      }

      // Add tooltip component
      parts.push(
        <Tooltip key={key++} title={tooltipContent} arrow placement="top">
          <span
            tabIndex={0} // eslint-disable-line jsx-a11y/no-noninteractive-tabindex
            style={{
              textDecoration: 'underline dotted',
              cursor: 'help',
            }}
            className="tooltip-trigger"
          >
            {trigger}
          </span>
        </Tooltip>,
      );

      lastIndex = match.index + match[0].length;
    }

    // Add remaining text
    if (lastIndex < text.length) {
      parts.push(text.slice(lastIndex));
    }

    // If we only have one part and it's a string, return it directly
    if (parts.length === 1 && typeof parts[0] === 'string') {
      return parts[0];
    }

    return <>{parts}</>;
  } catch (exception) {
    // If anything goes wrong, just return the original text
    console.warn('Failed to parse tooltip text:', exception);
    return text;
  }
};
